import React from 'react';

export interface JpegStatus {
    id?: number;
    is_generating: boolean;
    last_status_message?: string;
    current_step?: string;
    processed_files: number;
    total_files: number;
    error?: string;
    updated_at?: string;
}

interface JpegMonitoringModalProps {
    status: JpegStatus | null;
    onClose: () => void;
}

const STEPS = [
    { key: 'INIT', label: 'Inizializzazione' },
    { key: 'CLEANUP', label: 'Pulizia directory' },
    { key: 'GENERATE', label: 'Generazione JPEG dai TIFF' },
    { key: 'EXIF', label: 'Generazione report EXIF JPG' },
];

const STEP_ORDER = STEPS.map(s => s.key);

function getStepState(stepKey: string, currentStep: string | undefined, isGenerating: boolean, hasError: boolean): 'pending' | 'active' | 'done' | 'error' {
    if (!currentStep) return 'pending';

    if (currentStep === 'COMPLETED') return 'done';
    if (currentStep === 'ERROR' || hasError) {
        // If there's an error, we mark the current step as error if it matches
        if (currentStep === stepKey) return 'error';
        // Otherwise, if it was already passed, it's done, if not, it's pending
        const currentIdx = STEP_ORDER.indexOf(currentStep);
        const thisIdx = STEP_ORDER.indexOf(stepKey);
        if (thisIdx < currentIdx) return 'done';
        if (thisIdx === currentIdx) return 'error';
        return 'pending';
    }

    const currentIdx = STEP_ORDER.indexOf(currentStep);
    const thisIdx = STEP_ORDER.indexOf(stepKey);

    if (currentIdx === -1) return 'pending';

    if (thisIdx < currentIdx) return 'done';
    if (thisIdx === currentIdx) {
        if (isGenerating) return 'active';
        return 'error';
    }
    return 'pending';
}

const JpegMonitoringModal: React.FC<JpegMonitoringModalProps> = ({ status, onClose }) => {
    if (!status) return null;

    const currentStep = status.current_step;
    const isGenerating = status.is_generating;
    const isError = currentStep === 'ERROR' || !!status.error;
    const isCompleted = currentStep === 'COMPLETED';

    const progressPct = status.total_files > 0
        ? Math.round((status.processed_files / status.total_files) * 100)
        : 0;

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-content" style={{ maxWidth: '520px', padding: '32px' }}>
                <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '1.3rem' }}>
                    {isCompleted ? '✅ Generazione completata' : isError ? '❌ Errore generazione' : '⚙️ Generazione JPEG in corso...'}
                </h2>

                {/* Step checklist */}
                <div style={{ marginBottom: '24px' }}>
                    {STEPS.map(step => {
                        const state = getStepState(step.key, currentStep, isGenerating, isError);
                        const isProgressStep = step.key === 'GENERATE' || step.key === 'EXIF';
                        const isActiveProgress = isProgressStep && currentStep === step.key && status.total_files > 0;

                        return (
                            <div key={step.key} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 0',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                opacity: state === 'pending' ? 0.4 : 1,
                                transition: 'opacity 0.3s ease',
                            }}>
                                {/* Icon */}
                                <div style={{ width: '24px', textAlign: 'center', flexShrink: 0 }}>
                                    {state === 'done' && <span style={{ color: '#22c55e', fontSize: '1.1rem' }}>✓</span>}
                                    {state === 'active' && (
                                        <span style={{
                                            display: 'inline-block',
                                            width: '14px',
                                            height: '14px',
                                            border: '2px solid var(--primary-color)',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite',
                                        }} />
                                    )}
                                    {state === 'error' && <span style={{ color: '#ef4444', fontSize: '1.1rem' }}>✗</span>}
                                    {state === 'pending' && <span style={{ color: '#ccc', fontSize: '1rem' }}>○</span>}
                                </div>

                                {/* Label */}
                                <span style={{
                                    flex: 1,
                                    fontWeight: state === 'active' ? 600 : 400,
                                    color: state === 'error' ? '#ef4444' : state === 'done' ? '#22c55e' : 'inherit',
                                }}>
                                    {step.label}
                                </span>

                                {/* Progress badge */}
                                {isActiveProgress && (
                                    <span style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--primary-color)',
                                        fontWeight: 600,
                                        background: 'rgba(var(--primary-rgb, 99,102,241), 0.1)',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {status.processed_files} / {status.total_files}
                                    </span>
                                )}
                                {isProgressStep && state === 'done' && status.total_files > 0 && (
                                    <span style={{ fontSize: '0.8rem', color: '#22c55e', fontWeight: 600 }}>
                                        {status.total_files} file
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Progress bar */}
                {status.total_files > 0 && (currentStep === 'GENERATE' || currentStep === 'EXIF') && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '4px', color: '#666' }}>
                            <span>Avanzamento</span>
                            <span>{status.processed_files} / {status.total_files} ({progressPct}%)</span>
                        </div>
                        <div style={{ height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${progressPct}%`,
                                background: 'var(--primary-color)',
                                borderRadius: '3px',
                                transition: 'width 0.4s ease',
                            }} />
                        </div>
                    </div>
                )}

                {/* Status message or error */}
                {(status.last_status_message || status.error) && (
                    <div style={{
                        fontSize: '0.85rem',
                        color: isError ? '#ef4444' : '#666',
                        background: isError ? '#fef2f2' : '#f8f9fa',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        fontFamily: 'monospace',
                    }}>
                        {status.error || status.last_status_message}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                        className="btn btn-primary"
                        onClick={onClose}
                        disabled={isGenerating && !isError && !isCompleted}
                    >
                        {isGenerating && !isError && !isCompleted ? 'Generazione in corso...' : 'CHIUDI'}
                    </button>
                </div>
            </div>

            <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default JpegMonitoringModal;
